/** @format */

import { useState, useEffect, useMemo } from "react";
import TqPanelCard from "../components/tqPanelComponents/tqPanelCard";
import { useForm } from "react-hook-form";
import { TAXI_QUOTE_RD_DISPLAY } from "../../../config/config";
import usePanelStore from "../store/PanelStore";
import useQuoteStore from "../store/QuoteStore";
import useBuyStore from "../../buy/store/BuyStore";
import TqPanelUpdate from "../components/tqPanelComponents/tqPanelUpdate";
import Loader from "../../../shared/components/Loader";
import { getSchemeImage } from "../../../shared/utils/schemeImages";
import { TAXI_QUOTE_BRANDREF } from "../../../config/config";
import BuyStep from "../../buy/enums/BuyStep";
import { useNavigate } from "fusion-plugin-react-router";
import useAppStateStore from "../../../shared/store/AppStateStore";
import { HotjarEventNames } from "../../../shared/utils/hotjarEventNames";
import { TrackHotjarEvent } from "../../../shared/utils/trackHotjarEvent";
import QuoteStepHeading from "../../../shared/components/QuoteStepHeading";
import QuoteStep from "../enums/QuoteStep";
import TqPanelTable from "../components/tqPanelComponents/TqPanelTable";
import TqPanelMemberFeatures from "../components/tqPanelComponents/tqPanelMemberFeatures";
import ExplanationModal from "../components/explanationModal";
import TqPanelUpdateModal from "../components/tqPanelComponents/tqPanelUpdateModal";
import TqPolicyDateTimeModal from "../components/tqPanelComponents/tqPolicyDateTimeModal";
import TqPanelCoverageCard from "../components/tqPanelComponents/TqPanelCoverageCard";
import { fetchPolicyDetails } from "../services/quoteService";
import Button from "../../../shared/components/Button";
import { LuSlidersHorizontal } from "react-icons/lu";
import { buildTqPanelQuoteRows } from "../utils/quoteDisplayData";

const splitForSpace = (str) => {
  const [new1, new2] = str.split(" ");
  return (
    <>
      <span>{new1}</span>
      <span>{new2}</span>
    </>
  );
};

const excessReductions = [];

const TqPanel = () => {
  const {
    register,
    formState: { errors },
    watch,
    setValue,
    trigger,
  } = useForm();

  let STATE = useAppStateStore();

  const navigate = useNavigate();
  const [, setSelectedCardIndex] = useState(null);
  const [, setTriggerRender] = useState(0);
  const brandRef = TAXI_QUOTE_BRANDREF;

  const { get: getPanelData } = usePanelStore();
  const panelQuoteData = getPanelData()?.panelData ?? "";
  const { configs: quoteConfigs, rows: quoteRows } = useMemo(
    () => buildTqPanelQuoteRows(panelQuoteData),
    [panelQuoteData],
  );

  const { setObject } = useBuyStore();
  const riskObjectId = useQuoteStore.getState().quote?.risk?.riskObjectId;
  useEffect(() => {
    fetchPolicyDetails();
    if (STATE.memberRetry) {
      handleBuyClick(
        null,
        STATE.selectedCardIndex,
        STATE.selectedCard, // Use selectedCard instead of selectedCardQuote
      );
      STATE.setMemberRetry(false);
      STATE.setCurrentStep(BuyStep.TB_ADDON_RECOMMENDATIONS);
    }
  }, []);

  useEffect(() => {
    if (panelQuoteData?.insurerResults?.quoted === null) {
      navigate("/blocked-action?type=no-quote");
    }
  }, []);

  const handleCardClick = (index) => {
    setSelectedCardIndex(index);
    setValue("isSelected", index);
    setTriggerRender((prev) => prev + 1);
  };

  useEffect(() => {
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQ_PANEL_ENTERED);
  }, []);

  const handleBuyClick = (e, index, quote) => {
    STATE.setSelectedCard && STATE.setSelectedCard(quote); // Use setSelectedCard
    setSelectedCardIndex(index);
    setValue("isSelected", index);
    setValue("companyName", quote.companyName);
    setValue("pricePerYear", quote.pricePerYear);
    setValue("SelectedSchemeTableId", quote.schemeTableId);
    STATE.setSelectedSchemeTableId &&
      STATE.setSelectedSchemeTableId(quote.schemeTableId);

    // Create the request body
    const panelMemberReqBody = {
      RiskObjectId: riskObjectId,
      RiskObjectHistoryId: "1",
      BrandRef: brandRef,
      PreSelectedQuoteAddons: [],
      SelectedSchemeTableId: quote.schemeTableId || "",
      UseSessionData: true,
    };

    const documentsRequestBody = {
      brandRef,
      RiskObjectId: riskObjectId,
      RiskObjectHistoryId: 1,
      documentTagStage: "Quote",
      documentTagCategory: "PreSaleQuoteDocuments",
    };

    // Store it in BuyStore
    setObject("recommendationsData", null);
    setObject("panelMemberReqBody", panelMemberReqBody);
    setObject("documentsRequestBody", documentsRequestBody);
    setValue("panelMemberReqBody", panelMemberReqBody);

    if (!STATE.memberRetry) {
      STATE.setIspolicyModal(true);
    }
    if (STATE.memberRetry) {
      STATE.setActionOnTqPanel("QuoteSelected");
    }

    if (errors.companyName) {
      trigger("companyName");
    }
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQ_PANEL_SELECT_QUOTE_CLICKED);
  };

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [errors]);

  useEffect(() => {
    STATE.setShowModel(false);
    STATE.setFeaturesModal(false);
    STATE.setIspolicyModal(false);
    STATE.setQuoteLoading(false);
    const buyStore = useBuyStore.getState();
    buyStore.setObject("selectedAddons", []);
  }, []);

  useEffect(() => {
    STATE.setPreviousStep(QuoteStep.TQ_PANEL);
  }, []);

  const userSession = JSON.parse(sessionStorage.getItem("userSession"));
  const NAME = userSession?.forename;

  const handleModel = () => {
    STATE.setShowModel((prevState) => !prevState);
  };

  const onOpen = (quote) => {
    STATE.setSelectedCard(quote);
    STATE.setFeaturesModal(true);
  };

  const openExplanationModal = (heading, panelQuoteData) => {
    STATE.setExplanationData(panelQuoteData);
    STATE.setModalHeading(heading);
    STATE.setIsExplanationModal(true);
  };

  return (
    <>
      {STATE.quoteLoading && <Loader text="Selecting quote..." />}
      <div className="tw-flex tw-flex-col tw-gap-5 min-[1023px]:tw-flex-row">
        <div className="tw-block max-[1023px]:tw-hidden">
          <TqPanelUpdate watch2={watch} STATE={STATE} buySetValue={setValue} />
        </div>
        <div className="tw-min-w-0 tw-w-full min-[1023px]:tw-flex-1">
          <div className="tw-mb-[30px] tw-w-full tw-rounded-[14px] tw-border tw-border-solid tw-border-dis-border tw-bg-white tw-px-5 tw-py-5 md:tw-px-8 md:tw-py-8">
            <QuoteStepHeading
              title="Choose your quote"
              titleClassName="tw-mb-4"
              description={
                <>
                  Thank you, {NAME}. Here are your quotes based on the
                  information you have provided.
                  <br />
                  <br />
                  You can purchase and manage your policy online. If you have
                  any questions about your quote or cover options, please
                  contact our team on{" "}
                  <a href="tel:+448081606753" className="tw-text-dis-deep-blue">
                    <strong>0808 160 6753</strong>
                  </a>{" "}
                  and we'll be happy to help.
                  <br />
                  <br />
                  Reference number:{" "}
                  <strong>{panelQuoteData?.quoteReference ?? ""}</strong>
                </>
              }
            />
            <TqPanelCoverageCard />
            {errors.companyName && (
              <span className="tw-mt-[5px] tw-mb-[5px] tw-block tw-text-dis-small tw-text-dis-error">
                Buy a quote to proceed further!
              </span>
            )}
            <Button
              onClick={handleModel}
              type="button"
              variant="softQuote"
              size="small"
              leadingIcon={
                <LuSlidersHorizontal
                  className="tw-h-5 tw-w-5"
                  aria-hidden="true"
                />
              }
              className="tw-mt-5 tw-hidden max-[1023px]:tw-inline-flex"
            >
              Customise Quote Details
            </Button>
            <div className="tw-overflow-x-auto [border-collapse:collapse] max-[1023px]:tw-hidden">
              <div className="tw-w-full">
                <TqPanelTable
                  quoteRows={quoteRows}
                  configs={quoteConfigs}
                  getSchemeImage={getSchemeImage}
                  handleBuyClick={handleBuyClick}
                  onOpen={onOpen}
                  splitForSpace={splitForSpace}
                  excessReductions={excessReductions}
                />
              </div>
            </div>

            {quoteRows.length > 0 ? (
              quoteRows.map((quoteDisplay) => {
                const { index } = quoteDisplay;
                const isSelected = watch("isSelected") === index;
                return (
                  <TqPanelCard
                    key={index}
                    quoteDisplay={quoteDisplay}
                    STATE={STATE}
                    isSelected={isSelected}
                    onClick={() => handleCardClick(index)}
                    register={register}
                    errors={errors}
                    watch={watch}
                    trigger={trigger}
                    setValue={setValue}
                    onOpen={onOpen}
                    setSelectedSchemeTableId={STATE.setSelectedSchemeTableId}
                    openExplanationModal={openExplanationModal}
                    setSelectedCardIndex={setSelectedCardIndex}
                    setSelectedCard={STATE.setSelectedCard} // Use setSelectedCard
                  />
                );
              })
            ) : (
              <div className="tw-hidden max-[1023px]:tw-block">
                <h1 className="tw-mt-5">
                  Sorry we are unable to provide a price for you based on the
                  information provided
                </h1>
                {TAXI_QUOTE_RD_DISPLAY === true &&
                  panelQuoteData?.insurerResults?.referredOrDeclined?.map(
                    (insurer, index) => {
                      if (
                        insurer.declined &&
                        insurer.declineReasons &&
                        insurer.declineReasons.length > 0
                      ) {
                        return (
                          <div key={`insurer-${index}`}>
                            <h4>{insurer.schemeName}</h4>
                            <ul>
                              {insurer.declineReasons.map((reason, i) => (
                                <li key={i}>{reason}</li>
                              ))}
                            </ul>
                          </div>
                        );
                      } else {
                        return null;
                      }
                    },
                  )}
              </div>
            )}

            <div className="tw-mt-5 tw-mb-5 tw-flex tw-flex-col tw-gap-1 tw-border-t tw-border-dis-border tw-pt-4 tw-text-dis-min tw-leading-[1.5] tw-text-dis-text-muted">
              <p className="tw-m-0">
                All policies must be purchased and administered online and we
                have a dedicated online team to help you through any queries.
              </p>
              <p className="tw-mb-4">
                (Please quote your reference number:{" "}
                {panelQuoteData?.quoteReference ?? ""})
              </p>
              <p className="tw-m-0">* Please refer to policy details</p>
              <p className="tw-m-0">
                ** Terms and conditions apply. We search a selected panel of
                insurers, not the whole market, and we do not give advice.
                Prices include Insurance Premium Tax.
              </p>
            </div>
          </div>
        </div>
      </div>

      <TqPanelMemberFeatures />
      <ExplanationModal />
      <TqPanelUpdateModal />
      {STATE.ispolicyModal && <TqPolicyDateTimeModal />}
    </>
  );
};

export default TqPanel;
